/**
 * The consent page an AI tool's sign-in lands on (`/authorize` answers with `/connect?request=…`).
 * Out of the shell, like sign-in: the person came from another program and decides one thing here.
 * Consent is asked on every authorization, so this page is what stops a client someone else
 * registered from getting a code just because the person happens to be signed in.
 */

import { AlertTriangle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useAnswerMcpConsent, useMcpConsent } from '@/api/queries';
import { AuthCard } from '@/routes/SignInLocal';

import { ConsentCard } from './ConsentCard';

/** The server's sentence for a request that will not open; a link without one is no better. */
export const EXPIRED = 'This sign-in link has expired. Start again from your AI tool.';

/** Leaving the SPA for the AI tool's redirect address; a seam so tests can watch it. */
export const browser = {
  assign: (url: string) => window.location.assign(url),
};

function Refused({ message }: { message: string }) {
  return (
    <AuthCard>
      <div className="alert alert-warning" role="alert">
        <AlertTriangle aria-hidden="true" />
        <div className="a-body">{message}</div>
      </div>
    </AuthCard>
  );
}

const messageOf = (error: unknown) => (error instanceof Error ? error.message : EXPIRED);

export function ConnectPage() {
  const [params] = useSearchParams();
  const request = params.get('request');
  const consent = useMcpConsent(request);
  const answer = useAnswerMcpConsent();

  if (!request) return <Refused message={EXPIRED} />;
  if (consent.error) return <Refused message={messageOf(consent.error)} />;
  if (answer.error) return <Refused message={messageOf(answer.error)} />;
  if (!consent.data) {
    return (
      <AuthCard>
        <p className="hint" role="status">Loading…</p>
      </AuthCard>
    );
  }
  return (
    <ConsentCard
      consent={consent.data}
      // Still busy once answered: the browser is on its way out and a second answer would be refused.
      busy={answer.isPending || answer.isSuccess ? (answer.variables?.allow ?? null) : null}
      onAnswer={(allow, scopes) =>
        answer.mutate({ request, allow, ...(allow ? { scopes } : {}) }, { onSuccess: ({ redirect_url }) => browser.assign(redirect_url) })
      }
    />
  );
}
